import React, { useMemo } from 'react';
import { StaleBadge } from '../StalenessIndicator';
import InfoTooltip from '../InfoTooltip';
import { useNumberLocale, formatFixed } from '../../../utils/numberFormat';
import { useChartFormatters, getYearEndTicks, formatYearTickLabel } from '../chartFormatters';
import TailwindBarChart from '../../Tailwind/TailwindBarChart';
import { useChartPalette } from '../../Tailwind/chartColors';

function KPICard({
    label,
    value,
    format = 'number',
    icon,
    info,
}: {
    label: string;
    value: any;
    format?: string;
    icon: string;
    info?: React.ReactNode;
}) {
    const numberLocale = useNumberLocale();
    let displayValue = '—';
    let subValue: string | null = null;
    if (value !== null && value !== undefined) {
        switch (format) {
            case 'percent':
                displayValue = formatFixed(value * 100, numberLocale, 2) + '%';
                break;
            case 'multiple':
                displayValue = formatFixed(value, numberLocale, 2) + 'x';
                break;
            case 'months':
                displayValue = value + ' mo';
                subValue = '≈ ' + formatFixed(value / 12, numberLocale, 1) + ' yrs';
                break;
            case 'currency':
                displayValue = value.toLocaleString(numberLocale, { maximumFractionDigits: 0 });
                break;
            default:
                displayValue = typeof value === 'number' ? value.toLocaleString(numberLocale, { maximumFractionDigits: 2 }) : String(value);
        }
    }

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-[18px] text-slate-400">{icon}</span>
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
                {info && <InfoTooltip content={info} />}
            </div>
            <div className="text-2xl font-bold text-slate-900">{displayValue}</div>
            {subValue && <div className="text-xs text-slate-500 mt-0.5">{subValue}</div>}
        </div>
    );
}

const KPI_INFO: Record<string, React.ReactNode> = {
    irr: (
        <>
            <strong>IRR (XIRR)</strong> — Money-weighted Internal Rate of Return.
            <br />
            Annualized return that accounts for the exact timing of every cash flow.
            <br />
            <em>Formula:</em> rate r solving Σₜ (CFAEₜ − Drawdownsₜ) / (1 + r)^(days/365) = 0.
            <br />
            Solved by Newton-Raphson with Actual/365 day-count.
        </>
    ),
    npv: (
        <>
            <strong>NPV</strong> — Net Present Value.
            <br />
            Absolute value created by the portfolio in today's currency, discounted at the portfolio's hurdle rate (default 8%).
            <br />
            <em>Formula:</em> Σₜ (CFAEₜ − Drawdownsₜ) × 1 / (1 + d)^(days/365).
            <br />
            Positive NPV means value-accretive vs. the cost of capital.
        </>
    ),
    moic: (
        <>
            <strong>Equity Multiple (MOIC)</strong> — Money on Invested Capital.
            <br />
            How many times the invested capital has been multiplied — total performance, time-agnostic.
            <br />
            <em>Formula:</em> Σ Distributions / Σ Drawdowns.
            <br />
            <em>Note:</em> conceptually MOIC ≡ TVPI (includes residual NAV). Currently equal to DPI as residual is set to 0.
        </>
    ),
    dpi: (
        <>
            <strong>DPI</strong> — Distributions to Paid-In capital.
            <br />
            Realized return only: cash distributed back to investors as a multiple of cash called.
            <br />
            <em>Formula:</em> Σ Distributions / Σ Paid-In (Drawdowns).
            <br />
            DPI ≥ 1.0x means investors have at least gotten their money back in cash.
        </>
    ),
    tvpi: (
        <>
            <strong>TVPI</strong> — Total Value to Paid-In capital.
            <br />
            Total value (realized + unrealized) per unit of paid-in capital.
            <br />
            <em>Formula:</em> (Σ Distributions + Residual NAV) / Σ Paid-In.
            <br />
            <em>Note:</em> residual NAV is 0 in the current implementation, so TVPI temporarily equals DPI.
        </>
    ),
    payback_simple: (
        <>
            <strong>Payback (Simple)</strong> — Undiscounted payback period in months.
            <br />
            Time until the portfolio has returned its invested capital in raw cash. The point where the J-curve crosses zero.
            <br />
            <em>Formula:</em> first month i {'>'} 0 where cumsum(Distributions − Drawdowns) ≥ 0.
        </>
    ),
    payback_discounted: (
        <>
            <strong>Payback (Discounted)</strong> — Discounted payback period in months.
            <br />
            Same as simple payback but on cash flows discounted at the portfolio's rate. Always ≥ simple payback.
            <br />
            <em>Formula:</em> first month i {'>'} 0 where cumsum((Distributions − Drawdowns) × DFₜ) ≥ 0.
        </>
    ),
    peak_exposure: (
        <>
            <strong>Peak Exposure</strong> — Maximum equity at risk.
            <br />
            Maximum amount of unreturned capital outstanding at any point in time. Measures peak cash funding need.
            <br />
            <em>Formula:</em> max( cumsum(Drawdowns) − cumsum(Distributions) ) over the full horizon.
        </>
    ),
    avg_yield: (
        <>
            <strong>Avg Cash Yield</strong> — Average annual cash yield.
            <br />
            Annual cash yield delivered by the portfolio relative to invested capital — comparable to a dividend yield.
            <br />
            <em>Formula:</em> mean(annual distributions, excluding zero years) / Σ Drawdowns.
            <br />
            Years with zero distribution (partial / pre-deployment) are filtered out.
        </>
    ),
};

export default function OverviewTab({ data }: { data: any }) {
    const numberLocale = useNumberLocale();
    const { formatYAxisTick, formatTooltipValue } = useChartFormatters();
    const palette = useChartPalette();
    const kpis = data.kpis || {};

    const chartData = useMemo(() => {
        const ts = data.aggregated_cfae_ts || {};
        return Object.entries(ts).map(([date, value]: [string, any]) => ({
            date: date.substring(0, 7),
            value: value,
        }));
    }, [data.aggregated_cfae_ts]);

    const cfaeTicks = useMemo(() => getYearEndTicks(chartData.map(d => d.date)), [chartData]);

    return (
        <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KPICard label="IRR (XIRR)" value={kpis.irr_xirr} format="percent" icon="trending_up" info={KPI_INFO.irr} />
                <KPICard label="NPV" value={kpis.npv} format="currency" icon="attach_money" info={KPI_INFO.npv} />
                <KPICard label="Equity Multiple" value={kpis.equity_multiple_moic} format="multiple" icon="swap_vert" info={KPI_INFO.moic} />
                <KPICard label="DPI" value={kpis.dpi} format="multiple" icon="output" info={KPI_INFO.dpi} />
                <KPICard label="TVPI" value={kpis.tvpi} format="multiple" icon="assessment" info={KPI_INFO.tvpi} />
                <KPICard label="Payback (Simple)" value={kpis.payback_simple_months} format="months" icon="schedule" info={KPI_INFO.payback_simple} />
                <KPICard label="Payback (Discounted)" value={kpis.payback_discounted_months} format="months" icon="timer" info={KPI_INFO.payback_discounted} />
                <KPICard label="Peak Exposure" value={kpis.peak_equity_exposure} format="currency" icon="vertical_align_top" info={KPI_INFO.peak_exposure} />
                <KPICard label="Avg Cash Yield" value={kpis.avg_annual_cash_yield} format="percent" icon="savings" info={KPI_INFO.avg_yield} />
            </div>

            {/* Aggregated CFAE Chart */}
            {chartData.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Aggregated Cash Flow to Equity</h3>
                    <TailwindBarChart
                        data={chartData}
                        xAxisKey="date"
                        height={300}
                        showLegend={false}
                        series={[{
                            dataKey: 'value',
                            label: 'CFAE',
                            color: palette[0],
                            radius: [2, 2, 0, 0],
                        }]}
                        xAxisTicks={cfaeTicks}
                        xAxisTickFormatter={formatYearTickLabel}
                        xAxisTickFontSize={11}
                        yAxisTickFontSize={11}
                        yAxisTickFormatter={formatYAxisTick}
                        tooltipValueFormatter={formatTooltipValue}
                        brush={{ minDataPoints: 6 }}
                    />
                </section>
            )}

            {/* Asset Summary Table */}
            {data.assets && data.assets.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Asset Summary</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Model</th>
                                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Company</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Equity %</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">IRR</th>
                                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.assets.map((asset: any) => (
                                    <tr key={asset.id} className="border-b border-slate-100 last:border-b-0">
                                        <td className="px-4 py-2.5 font-medium text-slate-900">{asset.model_name}</td>
                                        <td className="px-4 py-2.5 text-slate-600">{asset.company_name}</td>
                                        <td className="px-4 py-2.5 text-right text-slate-700">{formatFixed(asset.equity_percentage * 100, numberLocale, 1)}%</td>
                                        <td className="px-4 py-2.5 text-right text-slate-700">
                                            {asset.internal_rate_return != null
                                                ? formatFixed(asset.internal_rate_return * 100, numberLocale, 2) + '%'
                                                : '—'}
                                        </td>
                                        <td className="px-4 py-2.5"><StaleBadge isStale={asset.is_stale} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Quarterly Summary Table — full portfolio cash-flow view */}
            {data.quarterly_summaries && data.quarterly_summaries.length > 0 && (
                <section className="bg-white rounded-xl border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Quarterly Summary</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Quarter</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Drawdowns</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Distributions</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Net CF</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Cumul. Drawn</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Unfunded</th>
                                    <th className="text-right px-4 py-2.5 font-medium text-slate-600">Assets</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.quarterly_summaries.map((q: any, i: number) => (
                                    <tr key={i} className="border-b border-slate-100 last:border-b-0">
                                        <td className="px-4 py-2 font-medium text-slate-900">{q.quarter}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.drawdowns?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.distributions?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.net_cash_flow?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.cumulative_drawdowns?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.unfunded_commitment?.toLocaleString(numberLocale, { maximumFractionDigits: 0 })}</td>
                                        <td className="px-4 py-2 text-right text-slate-700">{q.num_assets_active}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}
